import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Download, Plus, Trash2 } from 'lucide-react'
import {
  TAGEBUCH_BEHINDERUNG_ARTEN,
  TAGEBUCH_NIEDERSCHLAG,
  TAGEBUCH_WETTER_LAGEN,
  TAGEBUCH_WIND,
  tagebuchFachteil,
  tagebuchInhalt,
  tagebuchNaechsteNr,
  tagebuchSummen,
  type SiteDocDetail,
  type TagebuchInhalt,
} from '@symphenia/shared-types'
import { bestaetige } from '@/lib/dialoge'
import { fehlertext } from '@/lib/fehlertext'
import { FeldStatus, FeldZeile, Feldgruppe, feldKlasse, mitStand, pfadPatch, useFeld, useSpeicherstelle, useStellenDaten, type Datensatz, type Speicherstelle } from '@/lib/speichern'
import { cn } from '@/lib/utils'
import { bezugVon, tagebuchDeviceCache, tagebuchPrefill, updateSiteDoc } from '../../api/site-docs.api'
import { GegenzeichnungStand } from '../../components/GegenzeichnungStand'
import type { ErpDetailProps } from '../registry'
import { TagebuchDiktat } from './TagebuchDiktat'
import { TagebuchErpVorzeige } from './TagebuchErpVorzeige'
import { inhaltMit, mitVorbelegung, tagebuchDatensatz, zahlAnzeige } from './inhalt'
import { tagebuchTeilLabels } from './tagebuch-teile'

type Stelle = Speicherstelle<unknown>

/** Ändert den Inhalt über eine Operation auf dem jeweils aktuellen Stand (Listen als Ganzes, AP-36 8.3 a). */
type Aendere = (teil: string, f: (i: TagebuchInhalt) => Partial<TagebuchInhalt>) => void

// ── Felder ────────────────────────────────────────────────────────────────────

function Text({ stelle, pfad, label, typ = 'text', mehrzeilig, className, liste, folge }: { stelle: Stelle; pfad: string; label: string; typ?: 'text' | 'time' | 'date'; mehrzeilig?: boolean; className?: string; liste?: string; folge?: (w: string | null) => Datensatz }) {
  const f = useFeld<string | null>(stelle, pfad, { label, art: typ === 'date' ? 'datum' : 'text', folge })
  if (mehrzeilig) return <textarea className={cn(feldKlasse, 'min-h-16', className)} aria-label={label} {...f.props} />
  return <input type={typ} className={cn(feldKlasse, className)} aria-label={label} list={liste} {...f.props} />
}

function Zahl({ stelle, pfad, label, stellen = 0, className }: { stelle: Stelle; pfad: string; label: string; stellen?: number; className?: string }) {
  const f = useFeld<number | null>(stelle, pfad, { label, art: 'zahl', stellen })
  return <input className={cn(feldKlasse, 'text-right', className)} inputMode="decimal" aria-label={label} {...f.props} />
}

function Auswahl({ stelle, pfad, label, optionen, leer }: { stelle: Stelle; pfad: string; label: string; optionen: { value: string; label: string }[]; leer?: string }) {
  const f = useFeld<string | null>(stelle, pfad, { label, art: 'auswahl' })
  return (
    <select className={feldKlasse} aria-label={label} {...f.props}>
      {leer !== undefined && <option value="">{leer}</option>}
      {optionen.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  )
}

function Haken({ stelle, pfad, label }: { stelle: Stelle; pfad: string; label: string }) {
  const f = useFeld<boolean>(stelle, pfad, { label, art: 'schalter' })
  return (
    <label className="flex items-center gap-2 text-sm">
      <input type="checkbox" {...f.schalter} /> {label}
    </label>
  )
}

/** Ein einzelnes Feld außerhalb einer Gruppe — mit Beschriftung und Zustand. */
function EinzelFeld({ stelle, pfad, label, hilfe, mehrzeilig, typ }: { stelle: Stelle; pfad: string; label: string; hilfe?: string; mehrzeilig?: boolean; typ?: 'text' | 'time' }) {
  const f = useFeld<string | null>(stelle, pfad, { label })
  return (
    <FeldZeile label={label} status={f} hilfe={hilfe}>
      {mehrzeilig ? <textarea className={`${feldKlasse} min-h-16`} {...f.props} /> : <input type={typ ?? 'text'} className={feldKlasse} {...f.props} />}
    </FeldZeile>
  )
}

/** Eine Zeile einer Liste: ihre Felder bilden eine Gruppe (AP-36 — ein Listeneintrag). Vorbelegtes ist markiert. */
function Zeile({ stelle, label, spalten, markiert, onWeg, children }: { stelle: Stelle; label: string; spalten: string; markiert?: string | null; onWeg: () => void; children: ReactNode }) {
  const { t } = useTranslation()
  return (
    <Feldgruppe
      stelle={stelle}
      label={label}
      className={cn('grid items-start gap-2 rounded-md p-1', spalten, markiert && 'bg-sky-50 ring-1 ring-sky-300 dark:bg-sky-900/20 dark:ring-sky-700')}
      status={(s) => (
        <div className="col-span-full empty:hidden">
          <FeldStatus status={s} />
        </div>
      )}
    >
      {markiert && <span className="col-span-full text-xs font-medium text-sky-800 dark:text-sky-200">{markiert}</span>}
      {children}
      <button type="button" className="inline-flex h-9 items-center justify-center text-red-700 hover:text-red-900" aria-label={t('siteDocs.diary.removeRow')} title={t('siteDocs.diary.removeRow')} onClick={onWeg}>
        <Trash2 className="h-4 w-4" />
      </button>
    </Feldgruppe>
  )
}

function Teil({ titel, hinzu, onHinzu, hinweis, children }: { titel: string; hinzu?: string; onHinzu?: () => void; hinweis?: ReactNode; children: ReactNode }) {
  return (
    <section className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-medium">{titel}</h3>
        {onHinzu && (
          <button type="button" className="inline-flex h-8 items-center gap-1 rounded-md border px-2 text-xs" onClick={onHinzu}>
            <Plus className="h-3.5 w-3.5" /> {hinzu}
          </button>
        )}
      </div>
      {hinweis && <p className="text-xs text-muted-foreground">{hinweis}</p>}
      {children}
    </section>
  )
}

const ohne = <T,>(liste: readonly T[], i: number): T[] => liste.filter((_, j) => j !== i)

// ── Abschnitte ────────────────────────────────────────────────────────────────

function Wetter({ stelle, inhalt, aendere }: { stelle: Stelle; inhalt: TagebuchInhalt; aendere: Aendere }) {
  const { t } = useTranslation()
  const w = inhalt.wetter
  return (
    <Teil titel={t('siteDocs.diary.weatherTitle')}>
      <div className="flex flex-wrap gap-1.5" role="group" aria-label={t('siteDocs.diary.weatherTitle')}>
        {TAGEBUCH_WETTER_LAGEN.map((l) => {
          const an = w.lage.includes(l)
          return (
            <button
              key={l}
              type="button"
              aria-pressed={an}
              onClick={() => aendere('wetter', (i) => ({ wetter: { ...i.wetter, lage: an ? i.wetter.lage.filter((x) => x !== l) : TAGEBUCH_WETTER_LAGEN.filter((x) => x === l || i.wetter.lage.includes(x)) } }))}
              className={cn('h-9 rounded-md border px-3 text-sm', an && 'border-primary bg-primary/10 font-medium')}
            >
              {t(`siteDocs.diary.weather.${l}`)}
            </button>
          )
        })}
      </div>
      <div className="grid gap-2 sm:grid-cols-4">
        <label className="text-xs text-muted-foreground">
          {t('siteDocs.diary.tempMorning')}
          <Zahl stelle={stelle} pfad="wetter.tempMorgen" label={t('siteDocs.diary.tempMorning')} />
        </label>
        <label className="text-xs text-muted-foreground">
          {t('siteDocs.diary.tempNoon')}
          <Zahl stelle={stelle} pfad="wetter.tempMittag" label={t('siteDocs.diary.tempNoon')} />
        </label>
        <label className="text-xs text-muted-foreground">
          {t('siteDocs.diary.precipitationTitle')}
          <Auswahl stelle={stelle} pfad="wetter.niederschlag" label={t('siteDocs.diary.precipitationTitle')} leer="—" optionen={TAGEBUCH_NIEDERSCHLAG.map((n) => ({ value: n, label: t(`siteDocs.diary.precipitation.${n}`) }))} />
        </label>
        <label className="text-xs text-muted-foreground">
          {t('siteDocs.diary.windTitle')}
          <Auswahl stelle={stelle} pfad="wetter.wind" label={t('siteDocs.diary.windTitle')} leer="—" optionen={TAGEBUCH_WIND.map((n) => ({ value: n, label: t(`siteDocs.diary.wind.${n}`) }))} />
        </label>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={!!w.unterbrechung}
          onChange={(e) => {
            const an = e.target.checked
            aendere('wetter', (i) => ({ wetter: { ...i.wetter, unterbrechung: an ? { von: '', bis: '', grund: '' } : null } }))
          }}
        />
        {t('siteDocs.diary.interruptionWeather')}
      </label>
      {w.unterbrechung && (
        <Feldgruppe stelle={stelle} label={t('siteDocs.diary.interruption')} className="grid gap-2 sm:grid-cols-[8rem_8rem_1fr]">
          <Text stelle={stelle} pfad="wetter.unterbrechung.von" typ="time" label={t('siteDocs.diary.from')} />
          <Text stelle={stelle} pfad="wetter.unterbrechung.bis" typ="time" label={t('siteDocs.diary.to')} />
          <Text stelle={stelle} pfad="wetter.unterbrechung.grund" label={t('siteDocs.diary.reason')} />
        </Feldgruppe>
      )}
    </Teil>
  )
}

function Belegschaft({ stelle, inhalt, aendere, namenZeigen }: { stelle: Stelle; inhalt: TagebuchInhalt; aendere: Aendere; namenZeigen: boolean }) {
  const { t } = useTranslation()
  const b = inhalt.belegschaft
  const summen = tagebuchSummen(inhalt)
  const marke = (v?: boolean) => (v ? t('siteDocs.diary.prefilled') : null)
  return (
    <>
      <Teil titel={t('siteDocs.diary.staffOwn')} hinzu={t('siteDocs.diary.addRow')} onHinzu={() => aendere('belegschaft', (i) => ({ belegschaft: { ...i.belegschaft, eigene: [...i.belegschaft.eigene, { qualifikation: '', anzahl: 1, stunden: 0 }] } }))}>
        {b.eigene.map((e, n) => (
          <Zeile key={n} stelle={stelle} label={t('siteDocs.diary.staffOwn')} spalten="grid-cols-[1fr_5rem_6rem_2rem]" markiert={marke(e.vorbelegt)} onWeg={() => aendere('belegschaft', (i) => ({ belegschaft: { ...i.belegschaft, eigene: ohne(i.belegschaft.eigene, n) } }))}>
            <Text stelle={stelle} pfad={`belegschaft.eigene.${n}.qualifikation`} label={t('siteDocs.diary.qualification')} />
            <Zahl stelle={stelle} pfad={`belegschaft.eigene.${n}.anzahl`} label={t('siteDocs.diary.count')} />
            <Zahl stelle={stelle} pfad={`belegschaft.eigene.${n}.stunden`} label={t('siteDocs.diary.hours')} stellen={2} />
          </Zeile>
        ))}
      </Teil>
      {(namenZeigen || b.namen !== null) && (
        <Teil
          titel={t('siteDocs.diary.names')}
          hinzu={t('siteDocs.diary.addRow')}
          onHinzu={() => aendere('belegschaft', (i) => ({ belegschaft: { ...i.belegschaft, namen: [...(i.belegschaft.namen ?? []), ''] } }))}
        >
          {(b.namen ?? []).map((_, n) => (
            <Zeile key={n} stelle={stelle} label={t('siteDocs.diary.names')} spalten="grid-cols-[1fr_2rem]" onWeg={() => aendere('belegschaft', (i) => ({ belegschaft: { ...i.belegschaft, namen: ohne(i.belegschaft.namen ?? [], n) } }))}>
              <Text stelle={stelle} pfad={`belegschaft.namen.${n}`} label={t('siteDocs.diary.name')} />
            </Zeile>
          ))}
        </Teil>
      )}
      <Teil titel={t('siteDocs.diary.staffExternal')} hinzu={t('siteDocs.diary.addRow')} onHinzu={() => aendere('belegschaft', (i) => ({ belegschaft: { ...i.belegschaft, fremd: [...i.belegschaft.fremd, { firma: '', anzahl: 1, taetigkeit: null }] } }))}>
        {b.fremd.map((x, n) => (
          <Zeile key={n} stelle={stelle} label={t('siteDocs.diary.staffExternal')} spalten="grid-cols-[1fr_5rem_1fr_2rem]" markiert={marke(x.vorbelegt)} onWeg={() => aendere('belegschaft', (i) => ({ belegschaft: { ...i.belegschaft, fremd: ohne(i.belegschaft.fremd, n) } }))}>
            <Text stelle={stelle} pfad={`belegschaft.fremd.${n}.firma`} label={t('siteDocs.diary.company')} />
            <Zahl stelle={stelle} pfad={`belegschaft.fremd.${n}.anzahl`} label={t('siteDocs.diary.count')} />
            <Text stelle={stelle} pfad={`belegschaft.fremd.${n}.taetigkeit`} label={t('siteDocs.diary.activity')} />
          </Zeile>
        ))}
      </Teil>
      {summen.personen > 0 && (
        <p className="text-sm font-medium">
          {t('siteDocs.diary.totalShown', { personen: summen.personen, stunden: zahlAnzeige(summen.stunden) })}
        </p>
      )}
    </>
  )
}

function Geraete({ stelle, inhalt, aendere, artikel, listeId }: { stelle: Stelle; inhalt: TagebuchInhalt; aendere: Aendere; artikel: { id: string; number: string; name: string }[]; listeId: string }) {
  const { t } = useTranslation()
  return (
    <Teil titel={t('siteDocs.diary.equipment')} hinzu={t('siteDocs.diary.addRow')} onHinzu={() => aendere('geraete', (i) => ({ geraete: [...i.geraete, { bezeichnung: '', artikelnummer: null, anzahl: 1, stunden: null }] }))}>
      {artikel.length > 0 && (
        <datalist id={listeId}>
          {artikel.map((a) => (
            <option key={a.id} value={a.name}>
              {a.number}
            </option>
          ))}
        </datalist>
      )}
      {inhalt.geraete.map((g, n) => (
        <Zeile key={n} stelle={stelle} label={t('siteDocs.diary.equipment')} spalten="grid-cols-[1fr_7rem_5rem_6rem_2rem]" markiert={g.vorbelegt ? t('siteDocs.diary.prefilledLast') : null} onWeg={() => aendere('geraete', (i) => ({ geraete: ohne(i.geraete, n) }))}>
          <Text
            stelle={stelle}
            pfad={`geraete.${n}.bezeichnung`}
            label={t('siteDocs.diary.equipmentName')}
            liste={artikel.length ? listeId : undefined}
            // Aus dem Artikelstamm (`EQUIPMENT`): Nummer als Schnappschuss, die Zuordnung daneben (AP-46 BD-24).
            folge={
              artikel.length
                ? (w) => {
                    const a = artikel.find((x) => x.name === w)
                    return pfadPatch(`geraete.${n}`, { artikelId: a?.id ?? null, artikelnummer: a?.number ?? null })
                  }
                : undefined
            }
          />
          <span className="truncate py-1.5 text-xs text-muted-foreground" title={g.artikelnummer ?? undefined}>
            {g.artikelnummer ?? ''}
          </span>
          <Zahl stelle={stelle} pfad={`geraete.${n}.anzahl`} label={t('siteDocs.diary.count')} />
          <Zahl stelle={stelle} pfad={`geraete.${n}.stunden`} label={t('siteDocs.diary.hours')} stellen={2} />
        </Zeile>
      ))}
    </Teil>
  )
}

function Behinderungen({ stelle, inhalt, aendere, tag }: { stelle: Stelle; inhalt: TagebuchInhalt; aendere: Aendere; tag: string }) {
  const { t } = useTranslation()
  const setzeEine = (n: number, teil: Partial<TagebuchInhalt['behinderungen'][number]>, ohneMarken: ('fortgeschrieben' | 'artVorschlag')[]) =>
    aendere('behinderungen', (i) => ({
      behinderungen: i.behinderungen.map((b, j) => {
        if (j !== n) return b
        const neu = { ...b, ...teil }
        for (const m of ohneMarken) delete neu[m]
        return neu
      }),
    }))
  return (
    <Teil
      titel={t('siteDocs.diary.hindrances')}
      hinzu={t('siteDocs.diary.addHindrance')}
      onHinzu={() =>
        aendere('behinderungen', (i) => ({
          behinderungen: [...i.behinderungen, { nr: tagebuchNaechsteNr(i), kette: crypto.randomUUID(), art: 'SONSTIGES', text: '', seit: tag, bis: null, auswirkung: null, mehrkosten: false, angezeigtAm: null }],
        }))
      }
    >
      {inhalt.behinderungen.map((b, n) => (
        <div key={b.kette || n} className="space-y-1 rounded-md border-l-4 border-amber-500 bg-amber-50/60 p-2 dark:bg-amber-900/20">
          {b.fortgeschrieben && (
            <div className="flex flex-wrap items-center gap-2 text-xs text-sky-900 dark:text-sky-100">
              <span className="font-medium">{t('siteDocs.diary.carriedOver')}</span>
              <button type="button" className="h-7 rounded-md border px-2" onClick={() => setzeEine(n, { bis: null }, ['fortgeschrieben'])}>
                {t('siteDocs.diary.stillOngoing')}
              </button>
              <button type="button" className="h-7 rounded-md border px-2" onClick={() => setzeEine(n, { bis: tag }, ['fortgeschrieben'])}>
                {t('siteDocs.diary.endedToday')}
              </button>
            </div>
          )}
          {b.artVorschlag && (
            <div className="flex flex-wrap items-center gap-2 text-xs text-amber-900 dark:text-amber-100">
              <span className="font-medium">{t('siteDocs.diktat.hints.KIND_PROPOSED')}</span>
              <button type="button" className="h-7 rounded-md border px-2" onClick={() => setzeEine(n, {}, ['artVorschlag'])}>
                {t('siteDocs.diary.confirmKind')}
              </button>
            </div>
          )}
          <Zeile stelle={stelle} label={t('siteDocs.diary.hindrance', { nr: b.nr })} spalten="grid-cols-1 sm:grid-cols-[12rem_1fr_9rem_2rem]" onWeg={() => aendere('behinderungen', (i) => ({ behinderungen: ohne(i.behinderungen, n) }))}>
            <Auswahl stelle={stelle} pfad={`behinderungen.${n}.art`} label={t('siteDocs.diary.hindranceKind')} optionen={TAGEBUCH_BEHINDERUNG_ARTEN.map((a) => ({ value: a, label: t(`siteDocs.diary.hindranceKinds.${a}`) }))} />
            <Text stelle={stelle} pfad={`behinderungen.${n}.text`} label={t('siteDocs.diary.hindranceText')} />
            <Text stelle={stelle} pfad={`behinderungen.${n}.seit`} typ="date" label={t('siteDocs.diary.since')} />
            <div className="col-span-full grid gap-2 sm:grid-cols-[10rem_9rem_1fr]">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!b.bis} onChange={(e) => setzeEine(n, { bis: e.target.checked ? null : tag }, ['fortgeschrieben'])} /> {t('siteDocs.diary.ongoing')}
              </label>
              {b.bis ? <Text stelle={stelle} pfad={`behinderungen.${n}.bis`} typ="date" label={t('siteDocs.diary.until')} /> : <span />}
              <Text stelle={stelle} pfad={`behinderungen.${n}.auswirkung`} label={t('siteDocs.diary.effect')} />
            </div>
            <div className="col-span-full grid items-center gap-2 sm:grid-cols-[14rem_auto_9rem]">
              <Haken stelle={stelle} pfad={`behinderungen.${n}.mehrkosten`} label={t('siteDocs.diary.extraCosts')} />
              <span className="text-xs text-muted-foreground sm:text-right">{t('siteDocs.diary.notifiedOn')}</span>
              <Text stelle={stelle} pfad={`behinderungen.${n}.angezeigtAm`} typ="date" label={t('siteDocs.diary.notifiedOn')} />
            </div>
          </Zeile>
        </div>
      ))}
    </Teil>
  )
}

// ── Der Detailteil ────────────────────────────────────────────────────────────

/** Speicherstelle des Inhalts — eine je Bericht; geschrieben wird der Inhalt als Ganzes. */
function useTagebuchStelle(dok: SiteDocDetail, onGeaendert: ErpDetailProps['onGeaendert']): Stelle {
  const { t } = useTranslation()
  const ref = useRef<Stelle | null>(null)
  const stelle = useSpeicherstelle<SiteDocDetail>({
    schluessel: `tagebuch:${dok.id}`,
    bezeichnung: `${t('siteDocs.kinds.DIARY')} ${dok.number ?? ''}`.trim(),
    daten: dok,
    werte: tagebuchDatensatz,
    schreibe: async (patch, stand) => {
      const content = inhaltMit(ref.current?.daten ?? tagebuchDatensatz(dok), patch)
      const neu = await updateSiteDoc('DIARY', dok.id, { content }, mitStand(stand))
      onGeaendert(neu)
      return neu
    },
  }) as Stelle
  ref.current = stelle
  return stelle
}

function Entwurf({ dok, onGeaendert }: ErpDetailProps) {
  const { t } = useTranslation()
  const stelle = useTagebuchStelle(dok, onGeaendert)
  const inhalt = tagebuchInhalt(useStellenDaten(stelle))
  const tag = dok.documentDate.slice(0, 10)
  const [fehler, setFehler] = useState<string | null>(null)
  const [meldung, setMeldung] = useState<string | null>(null)
  const [vorrat, setVorrat] = useState<{ namenZeigen: boolean; articles: { id: string; number: string; name: string }[] }>({ namenZeigen: false, articles: [] })
  const zaehler = useRef(0)

  useEffect(() => {
    tagebuchDeviceCache()
      .then((v) => setVorrat({ namenZeigen: v.namenZeigen, articles: v.articles }))
      .catch(() => setVorrat({ namenZeigen: false, articles: [] }))
  }, [])

  const aendere = useCallback<Aendere>(
    (teil, f) => {
      setFehler(null)
      // Jede Operation eine eigene Kennung — zwei schnelle „Hinzufügen" ersetzen einander sonst (7.2).
      void stelle.sende({ kennung: `${teil}#${++zaehler.current}`, felder: [teil], labels: tagebuchTeilLabels(t), operation: (aktuell) => f(tagebuchInhalt(aktuell)) as Datensatz }).then((e) => {
        if (e.art === 'fehler') setFehler(e.text)
      })
    },
    [stelle, t],
  )

  const vorbelegen = async () => {
    setFehler(null)
    setMeldung(null)
    try {
      const v = await tagebuchPrefill({ ...bezugVon(dok), date: tag })
      const ersetzen = !!v.belegschaft && inhalt.belegschaft.eigene.length > 0 ? await bestaetige(t('siteDocs.diary.prefillReplaceStaff'), { ok: t('siteDocs.diary.prefillReplace') }) : true
      const vorher = inhalt
      const nachher = mitVorbelegung(vorher, v, ersetzen)
      const e = await stelle.sende({
        kennung: `vorbelegen#${++zaehler.current}`,
        felder: ['belegschaft', 'lieferungen', 'geraete', 'behinderungen'],
        labels: tagebuchTeilLabels(t),
        operation: (aktuell) => {
          const n = mitVorbelegung(tagebuchInhalt(aktuell), v, ersetzen)
          return { belegschaft: n.belegschaft, lieferungen: n.lieferungen, geraete: n.geraete, behinderungen: n.behinderungen }
        },
      })
      if (e.art === 'fehler') return setFehler(e.text)
      const teile = [
        v.belegschaft === null ? t('siteDocs.diary.prefillNoProjectTime') : v.buchungen === 0 ? t('siteDocs.diary.prefillNoBookings') : ersetzen ? t('siteDocs.diary.prefillStaff', { count: v.belegschaft.eigene.length }) : null,
        nachher.lieferungen.length > vorher.lieferungen.length ? t('siteDocs.diary.prefillDeliveries', { count: nachher.lieferungen.length - vorher.lieferungen.length }) : null,
        v.letzter ? t('siteDocs.diary.prefillLast', { nummer: v.letzter.number ?? '' }) : null,
      ].filter(Boolean)
      setMeldung(teile.join(' · ') || t('siteDocs.diary.prefillNothing'))
    } catch (err) {
      setFehler(fehlertext(err, t))
    }
  }

  const vorbelegtMarke = t('siteDocs.diary.prefilled')
  return (
    <div className="space-y-5">
      {fehler && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{fehler}</p>}
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" className="inline-flex h-8 items-center gap-1 rounded-md border px-3 text-sm" onClick={() => void vorbelegen()}>
          <Download className="h-4 w-4" /> {t('siteDocs.diary.prefill')}
        </button>
        <span className="text-xs text-muted-foreground">{meldung ?? t('siteDocs.diary.prefillHint')}</span>
      </div>
      {dok.can.dictate && <TagebuchDiktat dok={dok} stelle={stelle} />}

      <Teil titel={t('siteDocs.diary.workTime')}>
        <Feldgruppe stelle={stelle} label={t('siteDocs.diary.workTime')} className="grid max-w-sm grid-cols-2 gap-2">
          <Text stelle={stelle} pfad="arbeitszeit.beginn" typ="time" label={t('siteDocs.diary.begin')} />
          <Text stelle={stelle} pfad="arbeitszeit.ende" typ="time" label={t('siteDocs.diary.end')} />
        </Feldgruppe>
      </Teil>

      <Wetter stelle={stelle} inhalt={inhalt} aendere={aendere} />

      <EinzelFeld stelle={stelle} pfad="stillstand" label={t('siteDocs.diary.standstill')} hilfe={t('siteDocs.diary.standstillHint')} />

      <Belegschaft stelle={stelle} inhalt={inhalt} aendere={aendere} namenZeigen={vorrat.namenZeigen} />

      <Geraete stelle={stelle} inhalt={inhalt} aendere={aendere} artikel={vorrat.articles} listeId={`tagebuch-geraete-${dok.id}`} />

      <Teil titel={t('siteDocs.diary.work')} hinzu={t('siteDocs.diary.addRow')} onHinzu={() => aendere('leistungen', (i) => ({ leistungen: [...i.leistungen, ''] }))}>
        {inhalt.leistungen.map((_, n) => (
          <Zeile key={n} stelle={stelle} label={t('siteDocs.diary.work')} spalten="grid-cols-[1fr_2rem]" onWeg={() => aendere('leistungen', (i) => ({ leistungen: ohne(i.leistungen, n) }))}>
            <Text stelle={stelle} pfad={`leistungen.${n}`} label={t('siteDocs.diary.workItem')} />
          </Zeile>
        ))}
      </Teil>

      <Teil titel={t('siteDocs.diary.deliveries')} hinzu={t('siteDocs.diary.addRow')} onHinzu={() => aendere('lieferungen', (i) => ({ lieferungen: [...i.lieferungen, { text: '', lieferant: null, lieferschein: null }] }))}>
        {inhalt.lieferungen.map((l, n) => (
          <Zeile key={n} stelle={stelle} label={t('siteDocs.diary.deliveries')} spalten="grid-cols-[1fr_12rem_9rem_2rem]" markiert={l.vorbelegt ? vorbelegtMarke : null} onWeg={() => aendere('lieferungen', (i) => ({ lieferungen: ohne(i.lieferungen, n) }))}>
            <Text stelle={stelle} pfad={`lieferungen.${n}.text`} label={t('siteDocs.diary.deliveryText')} />
            <Text stelle={stelle} pfad={`lieferungen.${n}.lieferant`} label={t('siteDocs.diary.supplier')} />
            <Text stelle={stelle} pfad={`lieferungen.${n}.lieferschein`} label={t('siteDocs.diary.deliveryNote')} />
          </Zeile>
        ))}
      </Teil>

      <Behinderungen stelle={stelle} inhalt={inhalt} aendere={aendere} tag={tag} />

      <Teil titel={t('siteDocs.diary.orders')} hinzu={t('siteDocs.diary.addRow')} onHinzu={() => aendere('anordnungen', (i) => ({ anordnungen: [...i.anordnungen, { von: '', text: '' }] }))}>
        {inhalt.anordnungen.map((_, n) => (
          <Zeile key={n} stelle={stelle} label={t('siteDocs.diary.orders')} spalten="grid-cols-[14rem_1fr_2rem]" onWeg={() => aendere('anordnungen', (i) => ({ anordnungen: ohne(i.anordnungen, n) }))}>
            <Text stelle={stelle} pfad={`anordnungen.${n}.von`} label={t('siteDocs.diary.orderedBy')} />
            <Text stelle={stelle} pfad={`anordnungen.${n}.text`} label={t('siteDocs.diary.orderText')} />
          </Zeile>
        ))}
      </Teil>

      <EinzelFeld stelle={stelle} pfad="vorkommnisse" label={t('siteDocs.diary.incidents')} hilfe={t('siteDocs.diary.incidentsHint')} mehrzeilig />
      <EinzelFeld stelle={stelle} pfad="bemerkung" label={t('siteDocs.diary.remark')} mehrzeilig />
    </div>
  )
}

/**
 * AP-48 7. — **Detailteil Bautagesbericht im ERP.** Im Entwurf das Formular in Abschnitten (4.1), feldweise
 * ohne Knopf; der Inhalt geht als Ganzes über `PATCH /site-docs/diary/:id`. „Vorbelegen" übernimmt Belegschaft,
 * Lieferungen und „wie im letzten Bericht" als markierten Vorschlag. Ohne Bearbeitungsrecht lesend; nach dem
 * Abschluss Gegenzeichnungen und Einwände (6.3, 6.4).
 */
export function TagebuchErpDetail({ dok, onGeaendert }: ErpDetailProps) {
  const entwurf = dok.status === 'DRAFT' && !dok.heldByUserId && dok.can.edit
  if (entwurf) return <Entwurf dok={dok} onGeaendert={onGeaendert} />
  if (dok.contentHash) return <GegenzeichnungStand dok={dok} />
  return <TagebuchErpVorzeige fachteil={tagebuchFachteil(dok.content ?? {})} />
}
